import { useAuth } from '@/contexts/AuthContext'
import { Link, useNavigate } from 'react-router-dom'
import { Avatar, AvatarFallback } from '@/components/ui/avatar'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { ChevronRight, LogOut, Shield } from 'lucide-react'

interface TopNavProps {
  orgSlug?: string
  orgName?: string
  productId?: string
  productName?: string
  currentPage?: string
}

/* ── Element (03) Q mark — periodic-tile style with copper metallic fill ── */
function QMark({ size = 30 }: { size?: number }) {
  // Hex viewBox is 208×180 (with 8px shadow offset)
  const svgH = size
  const svgW = Math.round(size * (208 / 180))
  return (
    <div style={{ width: svgW, height: svgH, position: 'relative', flexShrink: 0 }}>
      <svg
        viewBox="0 0 208 180"
        width={svgW}
        height={svgH}
        style={{ display: 'block', overflow: 'visible' }}
        aria-hidden="true"
      >
        {/* shadow layer */}
        <path
          transform="translate(8,8)"
          fill="#7b4524"
          d="M60,0 L140,0 Q150,0 155,8.66 L195,77.34 Q200,86 195,94.66 L155,163.34 Q150,172 140,172 L60,172 Q50,172 45,163.34 L5,94.66 Q0,86 5,77.34 L45,8.66 Q50,0 60,0 Z"
        />
        {/* copper fill */}
        <path
          fill="#ad6334"
          d="M60,0 L140,0 Q150,0 155,8.66 L195,77.34 Q200,86 195,94.66 L155,163.34 Q150,172 140,172 L60,172 Q50,172 45,163.34 L5,94.66 Q0,86 5,77.34 L45,8.66 Q50,0 60,0 Z"
        />
      </svg>
      {/* atomic number — top left */}
      <span
        style={{
          position: 'absolute',
          top: '7%',
          left: '21%',
          fontSize: svgW * 0.155,
          fontWeight: 700,
          fontFamily: '"JetBrains Mono", ui-monospace, monospace',
          color: 'rgba(90,46,14,0.9)',
          lineHeight: 1,
          pointerEvents: 'none',
        }}
      >
        29
      </span>
      {/* Q glyph — centred */}
      <span
        style={{
          position: 'absolute',
          left: '48%',
          top: '49%',
          transform: 'translate(-50%, -50%)',
          fontSize: svgH * 0.52,
          fontFamily: '"DM Serif Display", "Iowan Old Style", Georgia, serif',
          fontWeight: 400,
          fontStyle: 'italic',
          lineHeight: 1,
          color: 'rgba(255,248,230,0.95)',
          pointerEvents: 'none',
        }}
      >
        Q
      </span>
    </div>
  )
}

export function TopNav({ orgSlug, orgName, productName, currentPage }: TopNavProps) {
  const { user, logout, isSuperAdmin } = useAuth()
  const navigate = useNavigate()

  const initials = user?.name
    ? user.name
        .split(' ')
        .map((n) => n[0])
        .join('')
        .toUpperCase()
        .slice(0, 2)
    : '?'

  const handleLogout = () => {
    logout()
    navigate('/login')
  }

  return (
    <header className="h-14 border-b bg-card flex items-center px-4 gap-3 sticky top-0 z-50">
      {/* Logo */}
      <Link to="/orgs" className="flex items-center gap-2.5 shrink-0">
        <QMark size={30} />
        <span
          style={{
            fontWeight: 800,
            letterSpacing: '0.02em',
            fontSize: 18,
            fontFamily: '"Plus Jakarta Sans", system-ui, sans-serif',
          }}
        >
          <span style={{ color: 'hsl(var(--copper))' }}>Q</span>OPR
        </span>
      </Link>

      {/* Breadcrumb */}
      <nav className="flex-1 flex items-center gap-1 text-sm text-muted-foreground overflow-hidden">
        {orgName && orgSlug && (
          <Link
            to={`/orgs/${orgSlug}`}
            className="hover:text-foreground transition-colors truncate max-w-[160px]"
          >
            {orgName}
          </Link>
        )}
        {productName && (
          <>
            <ChevronRight className="h-3 w-3 shrink-0" />
            <span className="truncate max-w-[160px] text-foreground font-medium">{productName}</span>
          </>
        )}
        {currentPage && !productName && (
          <>
            {orgName && <ChevronRight className="h-3 w-3 shrink-0" />}
            <span className="truncate max-w-[160px] text-foreground font-medium">{currentPage}</span>
          </>
        )}
        {currentPage && productName && (
          <>
            <ChevronRight className="h-3 w-3 shrink-0" />
            <span className="truncate max-w-[160px] text-foreground font-medium">{currentPage}</span>
          </>
        )}
      </nav>

      {/* User menu */}
      {user && (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button className="outline-none">
              <Avatar className="h-8 w-8 cursor-pointer">
                <AvatarFallback
                  className="text-xs font-semibold"
                  style={{
                    background: 'hsl(var(--copper-tint))',
                    color: 'hsl(var(--copper-tint-fg))',
                  }}
                >
                  {initials}
                </AvatarFallback>
              </Avatar>
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-48">
            <DropdownMenuLabel>
              <div className="font-medium">{user.name}</div>
              <div className="text-xs text-muted-foreground font-normal truncate">{user.email}</div>
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            {isSuperAdmin && (
              <DropdownMenuItem asChild>
                <Link to="/admin/users" className="flex items-center">
                  <Shield className="h-4 w-4 mr-2" />
                  Admin Panel
                </Link>
              </DropdownMenuItem>
            )}
            <DropdownMenuItem onClick={handleLogout} className="text-destructive focus:text-destructive">
              <LogOut className="h-4 w-4 mr-2" />
              Logout
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      )}
    </header>
  )
}
